import React, { useState, useEffect, useRef } from "react";

const CodeEditor = ({ content, filePath, onSave, sessionId, onDownloadFile, onDownloadAll, showDownload }) => {
  const [editorContent, setEditorContent] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  // Initialize editor content when content prop changes
  useEffect(() => {
    if (content !== undefined && content !== null) {
      setEditorContent(content);
      setHasChanges(false);
    }
  }, [content]);

  // Generate line numbers based on content
  const generateLineNumbers = (content) => {
    const lines = content.split("\n");
    return lines.map((_, index) => index + 1).join("\n");
  };

  // Update line numbers when content changes
  useEffect(() => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.textContent = generateLineNumbers(editorContent);
    }
  }, [editorContent]);

  // Sync scrolling between textarea and line numbers
  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setEditorContent(newContent);
    setHasChanges(newContent !== (content || ""));
  };

  const handleSave = async () => {
    if (!hasChanges || !filePath || !sessionId) return;

    setIsSaving(true);
    setSaveMessage("Saving...");

    try {
      // Debug: Log the content being saved to ensure it doesn't include line numbers
      console.log(
        "Saving content (first 200 chars):",
        editorContent.substring(0, 200)
      );
      console.log("Content length:", editorContent.length);
      console.log(
        "Line numbers content (first 50 chars):",
        lineNumbersRef.current?.textContent?.substring(0, 50)
      );

      const result = await onSave(filePath, editorContent);
      if (result) {
        setHasChanges(false);
        setSaveMessage("Saved successfully");
        setTimeout(() => setSaveMessage(""), 2000);
      } else {
        setSaveMessage("Save failed");
      }
    } catch (error) {
      console.error("Save error:", error);
      setSaveMessage("Save error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl+S or Cmd+S to save
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }

    // Handle tab key for indentation
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = e.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;

      // Insert tab or spaces
      const newValue =
        value.substring(0, start) + "    " + value.substring(end);
      setEditorContent(newValue);

      // Set cursor position after the inserted tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    }
  };

  // Show loading state
  return (
    <div className="code-editor">
      <div className="editor-header">
        <div className="file-info">
          <span className="file-icon">📄</span>
          <span className="file-title">{filePath || "Untitled"}</span>
          {/* <span className="file-language">{language.toUpperCase()}</span> */}
          {editorContent.length > 1024 * 1024 && (
            <span className="large-file-warning">
              ⚠️ Large file ({Math.round(editorContent.length / 1024)}KB)
            </span>
          )}
        </div>
        <div className="editor-actions">
          {saveMessage && (
            <span
              className={`save-message ${
                saveMessage.includes("successfully") ? "success" : "error"
              }`}
            >
              {saveMessage}
            </span>
          )}
          <button
            className={`save-button ${hasChanges ? "has-changes" : ""}`}
            onClick={handleSave}
            disabled={!hasChanges || isSaving || !filePath || !sessionId}
          >
            {isSaving ? "Saving..." : hasChanges ? "Save" : "Saved"}
          </button>
        </div>
      </div>
      <div className="editor-container">
        <div className="line-numbers" ref={lineNumbersRef}></div>
        <textarea
          ref={textareaRef}
          className="editor-content"
          value={editorContent}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          placeholder="Start typing your code..."
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
      </div>

      <div className="editor-footer">
        <div className="editor-info">
          <span>Line: {(editorContent || "").split("\n").length}</span>
          <span>Chars: {(editorContent || "").length}</span>
          <span>Size: {new Blob([editorContent || ""]).size} bytes</span>
          {editorContent.length > 1024 * 1024 && (
            <span className="performance-warning">Large file</span>
          )}
        </div>
        {showDownload && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {onDownloadFile && (
              <button
                onClick={onDownloadFile}
                style={{
                  padding: "3px 10px",
                  background: "#0d6efd",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Download This File
              </button>
            )}
            {onDownloadAll && (
              <button
                onClick={onDownloadAll}
                style={{
                  padding: "3px 10px",
                  background: "#2ea043",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Download All (ZIP)
              </button>
            )}
          </div>
        )}
        <div className="editor-shortcuts">
          <span>
            Ctrl+S to save • Tab for indentation • Ctrl+Tab to switch tabs •
            Ctrl+W to close tab
          </span>
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;
